import React, { useEffect } from 'react';
import { MainLayout } from '../layouts';
import { useSiteName, useSiteDomain, useSupportEmail, useContactEmail } from '../contexts/SiteConfigContext';
import analyticsService from '../services/analytics';
import { usePageDocumentTitle } from '../hooks/useDocumentTitle';

const TermsPage: React.FC = () => {
  usePageDocumentTitle('Terms and Conditions');
  const siteName = useSiteName();
  const siteDomain = useSiteDomain();
  const supportEmail = useSupportEmail();
  const contactEmail = useContactEmail();

  // Track page view when component mounts
  useEffect(() => {
    try {
      analyticsService.trackPageView('terms', undefined, new Date().toISOString().split('T')[0], {
        referrer: document.referrer || 'direct'
      });
      console.log('📊 TermsPage: Page view tracked');
    } catch (analyticsError) {
      console.warn('Analytics tracking failed:', analyticsError);
    }
  }, []);

  return (
    <MainLayout>
      <div className="bg-white p-6 rounded-lg shadow-card">
        <h1 className="text-2xl font-bold mb-6">Terms and Conditions</h1>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">1. Introduction</h2>
          <p className="mb-3">
            Welcome to {siteName}. These terms and conditions govern your use of our website and services.
            By accessing or using {siteName}, you agree to be bound by these terms. If you disagree with any part of these terms, 
            please do not use our website or services.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">2. Definitions</h2>
          <p className="mb-3">
            <strong>"{siteName}"</strong> refers to our website, accessible at {siteDomain}<br />
            <strong>"Service"</strong> refers to the horse racing tips and analysis provided through our website<br />
            <strong>"User"</strong> refers to individuals who access or use our website and services
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">3. General Disclaimer</h2>
          <p className="mb-3">
            {siteName} provides horse racing tips and opinions for informational and entertainment purposes only. 
            We do not facilitate or encourage actual betting on this website.
          </p>
          <p className="mb-3">
            This site does not offer gambling services and is not a wagering operator. Users are solely responsible 
            for their betting choices and must comply with the laws in their jurisdiction.
          </p>
          <p className="mb-3">
            This service is intended for users aged 18 and over. By accessing and using this website, 
            you confirm you are at least 18 years old.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">4. No Guarantee of Accuracy or Results</h2>
          <p className="mb-3">
            While we strive to provide accurate and well-researched tips, no guarantees are made regarding 
            the performance of any recommended selections. Past performance is not indicative of future results.
          </p>
          <p className="mb-3">
            All tips and analysis are provided as opinions only and should not be considered as professional 
            gambling advice. Users should conduct their own research before making any betting decisions.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">5. Subscription Plans and Pricing</h2>
          <p className="mb-3">
            {siteName} may offer both free and paid subscription plans. The specific features available in each plan 
            will be detailed on our website.
          </p>
          <p className="mb-3">
            We reserve the right to modify our subscription plans and pricing at any time. Any changes to subscription 
            fees will be communicated to users in advance and will take effect at the next billing cycle.
          </p>
          <p className="mb-3">
            Current pricing for all subscription plans is available on our website. All prices are in Australian Dollars (AUD) 
            unless otherwise specified.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">6. Payment Terms</h2>
          <p className="mb-3">
            By subscribing to a paid plan, you authorize {siteName} to charge the applicable subscription fee to your 
            designated payment method at the beginning of each billing cycle.
          </p>
          <p className="mb-3">
            Subscription fees are charged in advance on either a weekly or monthly basis, depending on the plan selected. 
            Your subscription will automatically renew at the end of each billing period unless you cancel it prior to the renewal date.
          </p>
          <p className="mb-3">
            You are responsible for maintaining accurate and up-to-date payment information. If we are unable to process 
            your payment, we may suspend or terminate your access to paid features until payment is successfully processed.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">7. Cancellation Policy</h2>
          <p className="mb-3">
            You may cancel your subscription at any time through your account settings or by contacting our customer support team.
          </p>
          <p className="mb-3">
            If you cancel your subscription, you will continue to have access to paid features until the end of your current 
            billing period. No refunds will be provided for the unused portion of your subscription period.
          </p>
          <p className="mb-3">
            {siteName} reserves the right to cancel or suspend any subscription for violation of these terms and conditions, 
            fraudulent activity, or any other reason at our sole discretion.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">8. Intellectual Property</h2>
          <p className="mb-3">
            All content on {siteName}, including but not limited to text, graphics, logos, images, and software, 
            is the property of {siteName} and is protected by copyright and other intellectual property laws.
          </p>
          <p className="mb-3">
            Users may not reproduce, distribute, modify, or create derivative works from any content on our website 
            without our express written permission.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">9. Disclaimer of Warranties</h2>
          <p className="mb-3">
            {siteName} provides its service on an "as is" and "as available" basis. We make no warranties, 
            expressed or implied, regarding the reliability, accuracy, or availability of our service.
          </p>
          <p className="mb-3">
            We do not guarantee that our service will be uninterrupted, timely, secure, or error-free.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">10. Limitation of Liability</h2>
          <p className="mb-3">
            {siteName} shall not be held liable for any losses incurred from the use of tips or content published on this website. 
            Users are solely responsible for their betting and financial decisions.
          </p>
          <p className="mb-3">
            {siteName} and its owners, employees, and affiliates shall not be liable for any direct, indirect, 
            incidental, special, consequential, or punitive damages resulting from your use of or inability to use our service.
          </p>
          <p className="mb-3">
            This includes, but is not limited to, damages for loss of profits, goodwill, data, or other intangible losses.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">11. Responsible Gambling</h2>
          <p className="mb-3">
            We support responsible gambling. If you choose to bet based on our tips, do so responsibly. 
            For help with gambling addiction, visit Gambling Help Online or call 1800 858 858.
          </p>
          <p className="mb-3">
            {siteName} promotes responsible gambling. We encourage users to:
          </p>
          <ul className="list-disc pl-6 mb-3">
            <li>Set a budget and stick to it</li>
            <li>Never chase losses</li>
            <li>Not gamble when under the influence of alcohol or when emotional</li>
            <li>Take regular breaks from gambling</li>
            <li>Seek help if gambling becomes a problem</li>
          </ul>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">12. Changes to Terms</h2>
          <p className="mb-3">
            {siteName} reserves the right to modify these terms at any time. We will notify users of any changes 
            by updating the date at the bottom of this page.
          </p>
          <p className="mb-3">
            Your continued use of our service after any changes indicates your acceptance of the modified terms.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">13. Governing Law</h2>
          <p className="mb-3">
            These terms shall be governed by and construed in accordance with the laws of Australia, 
            without regard to its conflict of law provisions.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">14. Contact Us</h2>
          <p className="mb-3">
            If you have any questions about these terms, please contact us at {contactEmail}.
          </p>
        </section>
        
        <div className="text-sm text-gray-500 mt-8">
          Last updated: May 15, 2025
        </div>
      </div>
    </MainLayout>
  );
};

export default TermsPage;
